import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import ActionState, { ActionStatus } from 'store/ActionState';
import { IDed } from 'gql/Request';
import {
  MsEntraIDGroupTargetAccess,
  CreateMsEntraIDGroupTargetAccessInput,
  GetMsEntraIDGroupTargetAccessOptions,
  ListMsEntraIDGroupTargetAccessOptions,
  UpdateMsEntraIDGroupTargetAccessInput,
} from 'gql/MsEntraIDGroupTargetAccess';

export type MsEntraIDGroupTargetAccessState = {
  msEntraIDGroupTargetAccess?: MsEntraIDGroupTargetAccess;
  msEntraIDGroupTargetAccesses: EntityState<MsEntraIDGroupTargetAccess>;
  loadingState: ActionState;

  mutationState: ActionState;
};

export const msEntraIDGroupTargetAccessAdapter = createEntityAdapter<MsEntraIDGroupTargetAccess>();

export const initialState: MsEntraIDGroupTargetAccessState = {
  msEntraIDGroupTargetAccesses: msEntraIDGroupTargetAccessAdapter.getInitialState(),
  loadingState: { status: ActionStatus.IDLE },
  mutationState: { status: ActionStatus.IDLE },
};

const MsEntraIDGroupTargetAccessSlice = createSlice({
  name: 'msEntraIDGroupTargetAccess',
  initialState,
  reducers: {
    // GET
    getMsEntraIDGroupTargetAccessByID: (
      state,
      _: PayloadAction<GetMsEntraIDGroupTargetAccessOptions>
    ): MsEntraIDGroupTargetAccessState => {
      return { ...state, loadingState: { status: ActionStatus.PROCESSING } };
    },
    getMsEntraIDGroupTargetAccessByIDSucceeded: (
      state,
      action: PayloadAction<MsEntraIDGroupTargetAccess | undefined>
    ): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        msEntraIDGroupTargetAccesses: msEntraIDGroupTargetAccessAdapter.upsertMany(
          { ...state.msEntraIDGroupTargetAccesses },
          action.payload ? [action.payload] : []
        ),
        loadingState: { status: ActionStatus.SUCCEEDED },
      };
    },
    getMsEntraIDGroupTargetAccessByIDFailed: (state, action: PayloadAction<Error>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },
    // LIST
    listMsEntraIDGroupTargetAccessForTargetOrGroup: (
      state,
      _: PayloadAction<ListMsEntraIDGroupTargetAccessOptions>
    ): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.PROCESSING },
        msEntraIDGroupTargetAccesses: msEntraIDGroupTargetAccessAdapter.getInitialState(),
      };
    },
    listNextPageMsEntraIDGroupTargetAccessForTargetOrGroup: (
      state,
      _: PayloadAction<ListMsEntraIDGroupTargetAccessOptions>
    ): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.PROCESSING },
      };
    },
    listMsEntraIDGroupTargetAccessForTargetOrGroupSucceeded: (
      state,
      action: PayloadAction<MsEntraIDGroupTargetAccess[]>
    ): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        msEntraIDGroupTargetAccesses: msEntraIDGroupTargetAccessAdapter.setMany(
          { ...state.msEntraIDGroupTargetAccesses },
          action.payload
        ),
        loadingState: { status: ActionStatus.SUCCEEDED },
      };
    },
    listMsEntraIDGroupTargetAccessForTargetOrGroupFailed: (
      state,
      action: PayloadAction<Error>
    ): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },
    // CREATE
    createMsEntraIDGroupTargetAccess: (
      state,
      _: PayloadAction<CreateMsEntraIDGroupTargetAccessInput>
    ): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.PROCESSING },
      };
    },
    createMsEntraIDGroupTargetAccessSucceeded: (state, _: PayloadAction<IDed>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.SUCCEEDED },
      };
    },
    createMsEntraIDGroupTargetAccessFailed: (state, action: PayloadAction<Error>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },
    // UPDATE
    updateMsEntraIDGroupTargetAccess: (
      state,
      _: PayloadAction<UpdateMsEntraIDGroupTargetAccessInput>
    ): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.PROCESSING },
      };
    },
    updateMsEntraIDGroupTargetAccessSucceeded: (state, _: PayloadAction<IDed>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.SUCCEEDED },
      };
    },
    updateMsEntraIDGroupTargetAccessFailed: (state, action: PayloadAction<Error>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },
    // DELETE
    deleteMsEntraIDGroupTargetAccess: (state, _: PayloadAction<string>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.PROCESSING },
      };
    },
    deleteMsEntraIDGroupTargetAccessSucceeded: (state, _: PayloadAction<IDed>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.SUCCEEDED },
      };
    },
    deleteMsEntraIDGroupTargetAccessFailed: (state, action: PayloadAction<Error>): MsEntraIDGroupTargetAccessState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },
    // CLEAR
    clearState: () => {
      return initialState;
    },
  },
});

export default MsEntraIDGroupTargetAccessSlice;
